import { CheckCircle2, AlertTriangle, ShieldCheck, Cpu, ClipboardList, Ban } from "lucide-react";

// Meta de estado de readiness. La readiness SIEMPRE proviene del backend; el frontend sólo proyecta.
export const READINESS_META = {
  listo: { label: "Listo para monitoreo", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  borrador: { label: "Borrador · incompleto", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  suspendido: { label: "Suspendido", cls: "bg-slate-200 text-slate-600 border-slate-300" },
};

export function ReadinessBadge({ estado, testId, etiqueta }) {
  const m = READINESS_META[estado] || READINESS_META.borrador;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${m.cls}`}>
      {estado === "listo" ? <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> : <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />}
      {etiqueta || m.label}
    </span>
  );
}

// v2.9.9: badge DOMINANTE para personas inactivas (bajas históricas). El readiness histórico
// puede existir internamente, pero para la UI operativa el estado dominante de una baja es INACTIVA.
export function InactivaBadge({ testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
      <Ban className="h-3.5 w-3.5" strokeWidth={2} /> Inactiva
    </span>
  );
}

const AXES = [
  ["admin_operativo", "Administrativo / operativo", ShieldCheck],
  ["tecnico_cobertura", "Técnico / cobertura", Cpu],
  ["plan_respuesta", "Plan de respuesta", ClipboardList],
];

// Panel de las TRES dimensiones (v2.9.8). Muestra faltantes concretos y por qué no está listo.
export function ReadinessPanel({ readiness, testId }) {
  if (!readiness) return null;
  const tid = (s) => (testId ? `${testId}-${s}` : undefined);
  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ReadinessBadge estado={readiness.estado} testId={tid("estado")} etiqueta={readiness.etiqueta_estado} />
        <span className="text-xs text-slate-400">Modalidad: {readiness.modalidad === "institucional" ? "Institucional" : "Residencial"}</span>
      </div>
      {/* v2.13.4 — estado POR SERVICIO contratado: un «listo» de recordatorios no debe leerse como
          si la persona tuviera sensores. Sin alcance configurado (legacy) no se muestra nada nuevo. */}
      {(readiness.servicios || []).length > 0 && (
        <div data-testid={tid("servicios")} className="flex flex-wrap gap-2">
          {Object.entries(readiness.por_servicio || {}).map(([srv, v]) => (
            <span key={srv} data-testid={tid(`servicio-${srv}`)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${v.completo ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
              {v.completo ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {v.completo ? v.etiqueta : `${v.etiqueta}: ${v.faltantes.length} faltante(s)`}
            </span>
          ))}
          {!(readiness.servicios || []).includes("monitoreo_sensores") && (
            <span data-testid={tid("monitoreo-no-contratado")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500">
              <Ban className="h-3.5 w-3.5" /> Monitoreo por sensores: no contratado
            </span>
          )}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        {AXES.map(([key, titulo, Icon]) => {
          const eje = readiness[key] || { completo: false, faltantes: [] };
          const noAplica = eje.aplica === false;
          return (
            <div key={key} data-testid={tid(key)} className={`rounded-xl border p-4 ${noAplica ? "border-slate-200 bg-slate-50" : eje.completo ? "border-emerald-100 bg-emerald-50/40" : "border-amber-100 bg-amber-50/40"}`}>
              <p className="flex items-center gap-2 text-sm font-medium text-[#012472]">
                <Icon strokeWidth={1.6} className="h-4 w-4" />
                <span className={`h-2 w-2 rounded-full ${noAplica ? "bg-slate-400" : eje.completo ? "bg-emerald-500" : "bg-amber-500"}`} />
                {titulo}
              </p>
              <p className="text-xs mt-1 text-slate-500">
                {noAplica ? `No aplica · ${eje.no_aplica_motivo || "no corresponde a los servicios contratados"}`
                  : eje.completo ? "Completo" : `${(eje.faltantes || []).length} faltante(s)`}
              </p>
              {!noAplica && (eje.faltantes || []).length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-amber-700 list-disc pl-4">
                  {eje.faltantes.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      {readiness.estado !== "listo" && (
        <p data-testid={tid("porque")} className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {(readiness.servicios || []).length > 0
            ? "Aún no está listo: faltan requisitos de los servicios contratados (sólo se exige lo aplicable a cada servicio)."
            : <>Aún no está <b>Listo para monitoreo</b>: se requieren las tres dimensiones completas (conjunción estricta).</>}
        </p>
      )}
      {(readiness.avisos || []).length > 0 && (
        <ul className="space-y-1 text-xs text-slate-500 list-disc pl-4">
          {readiness.avisos.map((x, i) => <li key={i}>Recomendación: {x}</li>)}
        </ul>
      )}
    </div>
  );
}
